/**
 * CORS and iframe embedding service
 * Handles communication with parent frames and CORS configuration
 */

export class CorsService {
  private static allowedOrigins: string[] = [];

  /**
   * Initialize allowed origins from environment variable
   */
  private static initializeAllowedOrigins(): void {
    if (this.allowedOrigins.length > 0) return; // Already initialized

    // Get origins from environment variable (comma-separated)
    const corsOrigins = process.env.REACT_APP_CORS_ORIGIN;
    
    if (corsOrigins) {
      // Split by comma and clean up whitespace
      this.allowedOrigins = corsOrigins
        .split(',')
        .map(origin => origin.trim())
        .filter(origin => origin.length > 0);
    }

    // Add default development origins
    const devOrigins = [
      'http://localhost:3000',
      'http://localhost:3001', 
      'http://127.0.0.1:3000',
      'https://localhost:3000'
    ];

    // In development, add dev origins
    if (process.env.NODE_ENV === 'development') {
      this.allowedOrigins.push(...devOrigins);
    }

    // Only log CORS origins in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('🌐 CORS allowed origins:', this.allowedOrigins);
    }
  }

  /**
   * Check if the current context is embedded in an iframe
   */
  static isEmbedded(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to CORS, we're probably embedded
      return true;
    }
  }

  /**
   * Validate if origin is allowed
   */
  static isOriginAllowed(origin: string): boolean {
    this.initializeAllowedOrigins();

    // In development, allow all origins
    if (process.env.NODE_ENV === 'development') {
      return true;
    }

    // Check for wildcard permission
    if (this.allowedOrigins.includes('*')) {
      return true;
    }

    // Check exact match
    if (this.allowedOrigins.includes(origin)) {
      return true;
    }

    // Check wildcard domains (e.g., *.goicon.dev)
    return this.allowedOrigins.some(allowedOrigin => {
      if (allowedOrigin.startsWith('*.')) {
        const domain = allowedOrigin.substring(2); // Remove *.
        return origin.endsWith('.' + domain) || origin === domain;
      }
      return false;
    });
  }

  /**
   * Send message to parent frame
   */
  static postMessageToParent(data: any, targetOrigin: string = '*'): void {
    if (this.isEmbedded() && window.parent) {
      try {
        window.parent.postMessage(data, targetOrigin);
      } catch (error) {
        console.error('Failed to send message to parent frame:', error);
      }
    }
  }

  /**
   * Listen for messages from parent frame
   */
  static addMessageListener(callback: (event: MessageEvent) => void): () => void {
    const wrappedCallback = (event: MessageEvent) => {
      // Validate origin in production
      if (process.env.NODE_ENV === 'production' && 
          !this.isOriginAllowed(event.origin)) {
        console.warn('Blocked message from unauthorized origin:', event.origin);
        return;
      }

      callback(event);
    };

    window.addEventListener('message', wrappedCallback);

    // Return cleanup function
    return () => {
      window.removeEventListener('message', wrappedCallback);
    };
  }

  /**
   * Notify parent frame of dashboard events
   */
  static notifyParent(eventType: string, data?: any): void {
    this.postMessageToParent({
      type: 'DASHBOARD_EVENT',
      eventType,
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Request authentication token from parent frame
   */
  static requestAuthToken(): void {
    this.postMessageToParent({
      type: 'REQUEST_AUTH_TOKEN',
      timestamp: Date.now()
    });
  }

  /**
   * Notify parent of authentication status
   */
  static notifyAuthStatus(status: 'success' | 'error', message?: string): void {
    this.postMessageToParent({
      type: 'AUTH_STATUS',
      status,
      message,
      timestamp: Date.now()
    });
  }

  /**
   * Notify parent of data loading status
   */
  static notifyLoadingStatus(loading: boolean, message?: string): void {
    this.postMessageToParent({
      type: 'LOADING_STATUS',
      loading,
      message,
      timestamp: Date.now()
    });
  }

  /**
   * Configure CORS headers for fetch requests
   */
  static getCorsHeaders(): HeadersInit {
    // Do NOT send any Access-Control-* headers from the browser; those are response headers only
    // Keep this minimal to avoid triggering restrictive preflights
    return {
      'Accept': 'application/json'
    };
  }

  /** Default cap on how long a request may hang before it is aborted. */
  private static readonly DEFAULT_TIMEOUT_MS = 15_000;

  /**
   * Make a CORS-enabled fetch request with a default timeout.
   *
   * Without this, a stalled upstream (e.g. a goal add/complete/delete that the
   * proxy never answers) leaves the calling UI spinner running forever. If the
   * caller already supplies its own AbortSignal it is assumed to manage its own
   * timeout, so this wrapper leaves it alone.
   */
  static async corsEnabledFetch(
    url: string,
    options: RequestInit = {},
    timeoutMs: number = CorsService.DEFAULT_TIMEOUT_MS
  ): Promise<Response> {
    const corsOptions: RequestInit = {
      ...options,
      mode: 'cors',
      credentials: 'omit', // Don't send cookies for CORS
      headers: {
        ...this.getCorsHeaders(),
        ...options.headers
      }
    };

    if (corsOptions.signal) {
      // Caller owns the abort lifecycle; don't impose ours.
      return fetch(url, corsOptions);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, { ...corsOptions, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Initialize CORS service
   */
  static initialize(): () => void {
    // Only log CORS initialization in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('Initializing CORS service...');
      console.log('Embedded mode:', this.isEmbedded());
      console.log('Environment:', process.env.NODE_ENV);
    }

    // Set up message listener for parent communication
    const cleanup = this.addMessageListener((event) => {
      // Only log parent messages in development
      if (process.env.NODE_ENV !== 'production') {
        console.log('Received message from parent:', event.data);
      }

      switch (event.data?.type) {
        case 'AUTH_TOKEN':
          // Handle auth token from parent
          if (event.data.token) {
            (window as any).authToken = event.data.token;
            // Trigger app refresh if needed
            if ((window as any).refreshAuth) {
              (window as any).refreshAuth();
            }
          }
          break;

        case 'THEME_UPDATE':
          // Handle theme updates from parent
          if (event.data.theme) {
            document.documentElement.setAttribute('data-theme', event.data.theme);
          }
          break;

        case 'RESIZE':
          // Handle resize requests from parent
          if (event.data.height) {
            document.body.style.height = `${event.data.height}px`;
          }
          break;
      }
    });

    // Notify parent that dashboard is ready
    if (this.isEmbedded()) {
      this.postMessageToParent({
        type: 'DASHBOARD_READY',
        timestamp: Date.now()
      });
    }

    return cleanup;
  }
}







